import { report } from "./telemetry";

/**
 * A tap on the tab already open scrolls its list to the top (2026-10-02 triage, item 8: the
 * convention that replaced the floating back-to-top arrow). The board listens for the end of that
 * scroll to stack its pile again, as the arrow did (docs/product.md "Pile (2026-09-30)").
 */

let animation = 0;
let stopCurrent: (() => void) | null = null;

/** Run once a tap on the open tab has scrolled the screen to the top (the open board gathers its pile then). */
const topListeners = new Set<() => void>();
export function onScreenTop(fn: () => void): () => void {
  topListeners.add(fn);
  return () => topListeners.delete(fn);
}
function reachedTop(): void {
  topListeners.forEach((fn) => fn());
}

/**
 * Whether the scroll to the top is under way. A board leaves its pile to it then: the pile is
 * gathered once the scroll is over, so the scroll it makes must not count as the reader scrolling
 * away from the pile (src/views/Board.tsx).
 */
export function scrollingToTop(): boolean {
  return stopCurrent !== null;
}

/**
 * The element that scrolls. The document never does (see the layout notes in styles.css); each
 * screen's content pane is its own scroller and is replaced on navigation, so it is looked up on
 * every use rather than held.
 */
function scroller(): HTMLElement | null {
  return document.querySelector<HTMLElement>(".screen > .content, .gate");
}

/**
 * Scrolls the current screen to the top (the open board's fold, when it has one) with an ease-out driven by requestAnimationFrame (smooth,
 * and unlike `behavior: "smooth"` it says when it is over). The screen is an inner scroller, so on
 * iOS a tap that lands while it is still coasting stops the coast, which lets this start at once.
 * `done` runs once the scroll is over (at the top, or cut short by a new touch).
 */
export function scrollScreenToTop(done?: () => void): void {
  cancelAnimationFrame(animation);
  stopCurrent?.();
  const el = scroller();
  if (!el) return done?.();
  const from = el.scrollTop;
  // The open board's top is its fold (src/board-fold.ts): Next up, with the notice board above it.
  const to = Number(el.dataset.foldTop) || 0;
  if (from === to || (to === 0 && from <= 0)) return done?.();
  // Nothing to animate for a hidden document, and its animation frames would never run anyway.
  if (document.hidden || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    el.scrollTop = to;
    return done?.();
  }
  const duration = Math.min(600, Math.max(250, Math.abs(from - to) / 4));
  let start = 0;
  // Give way if the user starts scrolling mid-animation; the tap that started it arrives too, so
  // ignore anything in the first moment.
  const startedAt = Date.now();
  const cancel = () => {
    if (Date.now() - startedAt < 150) return;
    cancelAnimationFrame(animation);
    stop();
  };
  let over = false;
  const stop = () => {
    window.removeEventListener("wheel", cancel);
    window.removeEventListener("touchstart", cancel);
    if (stopCurrent === stop) stopCurrent = null;
    if (over) return;
    over = true;
    done?.();
  };
  stopCurrent = stop;
  window.addEventListener("wheel", cancel, { passive: true });
  window.addEventListener("touchstart", cancel, { passive: true });
  const step = (now: number) => {
    if (!start) start = now;
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - (1 - t) ** 3;
    el.scrollTop = Math.round(from + (to - from) * eased);
    if (t < 1) animation = requestAnimationFrame(step);
    else stop();
  };
  animation = requestAnimationFrame(step);
}

/** The open tab was tapped again: its list goes to the top and a fanned pile stacks once there. */
export function tapOpenTab(tab: string): void {
  report("tab.top", tab, { tab, from: scroller()?.scrollTop ?? 0 });
  scrollScreenToTop(reachedTop);
}
